import React from 'react';
import { useLocation } from 'react-router-dom';
import ProductPage from '../components/seo/ProductPage';
import { isEnglishPath } from '../utils/i18nRoutes';

const ALTERNATES = [
  { hreflang: 'es', href: '/klinloop' },
  { hreflang: 'en', href: '/en/klinloop' },
  { hreflang: 'x-default', href: '/klinloop' },
];

const CONTENT = {
  es: {
    seoTitle: 'KlinLoop | Limpiar biogás con un residuo | Valoriza',
    seoDescription: 'PoC de un nuevo material de desulfuración de biogás. El adsorbente sale del reciclaje de pilas alcalinas. Se prueba en entorno real de planta.',
    breadcrumbLabel: 'KlinLoop',
    tag: 'Valorización de residuos',
    titleMain: 'KlinLoop: limpiar biogás con un',
    titleAccent: 'residuo',
    lead: 'PoC de un nuevo material de desulfuración de biogás. El adsorbente sale del reciclaje de pilas alcalinas. Se prueba en entorno real de planta.',
    problema: 'La desulfuración del biogás es un paso crítico para su valorización energética. Los métodos convencionales a menudo requieren reactivos costosos o generan subproductos difíciles de gestionar. Existe la necesidad de soluciones más sostenibles y eficientes.',
    comoFunciona: 'KlinLoop ha desarrollado un adsorbente para la desulfuración de biogás cuya particularidad es que se fabrica a partir del reciclaje de pilas alcalinas. Este Proof of Concept (PoC) ha sido probado en un entorno real de planta de biogás, demostrando la viabilidad técnica del material para eliminar el sulfuro de hidrógeno y mejorar la calidad del biogás, utilizando un residuo como materia prima.',
    paraQuien: 'Plantas de biogás, gestores de residuos y empresas del sector energético que buscan optimizar la purificación del biogás con soluciones más circulares y eficientes. Departamentos de I+D interesados en valorización de residuos industriales.',
    enlaces: [{ label: 'Nuestros proyectos de I+D+i', to: '/proyectos' }, { label: 'Retos: batalla social — biometano y valorización energética', to: '/retos' }],
    ctaText: '¿Tienes un reto parecido?',
  },
  en: {
    seoTitle: 'KlinLoop | Cleaning biogas with a waste product | Valoriza',
    seoDescription: 'PoC of a new biogas desulfurization material. The adsorbent comes from recycling alkaline batteries. Tested in a real plant environment.',
    breadcrumbLabel: 'KlinLoop',
    tag: 'Waste valorization',
    titleMain: 'KlinLoop: cleaning biogas with a',
    titleAccent: 'waste product',
    lead: 'PoC of a new biogas desulfurization material. The adsorbent comes from recycling alkaline batteries. Tested in a real plant environment.',
    problema: 'Biogas desulfurization is a critical step for its energy valorization. Conventional methods often require costly reagents or generate byproducts that are difficult to manage. There is a need for more sustainable and efficient solutions.',
    comoFunciona: 'KlinLoop has developed an adsorbent for biogas desulfurization whose distinctive feature is that it\u2019s made from recycled alkaline batteries. This Proof of Concept (PoC) has been tested in a real biogas plant environment, demonstrating the technical viability of the material for removing hydrogen sulfide and improving biogas quality, using a waste product as the raw material.',
    paraQuien: 'Biogas plants, waste managers and energy-sector companies looking to optimize biogas purification with more circular, efficient solutions. R&D departments interested in industrial waste valorization.',
    enlaces: [{ label: 'Our R&D projects', to: '/en/projects' }, { label: 'Challenges: the biomethane and energy-recovery social battle', to: '/en/challenges' }],
    ctaText: 'Do you have a similar challenge?',
  },
};

export default function KlinLoopPage() {
  const { pathname } = useLocation();
  const locale = isEnglishPath(pathname) ? 'en' : 'es';
  const c = CONTENT[locale];
  const path = locale === 'en' ? '/en/klinloop' : '/klinloop';

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: 'KlinLoop',
    description: c.seoDescription,
    url: `https://innovation.valorizasm.com${path}`,
    brand: { '@id': 'https://innovation.valorizasm.com/#organization' },
  };

  return (
    <ProductPage
      testId="klinloop-page"
      path={path}
      locale={locale}
      alternates={ALTERNATES}
      jsonLd={[jsonLd]}
      problemaLabel={locale === 'en' ? 'The problem' : 'El problema'}
      comoFuncionaLabel={locale === 'en' ? 'How it works' : 'Cómo funciona'}
      cifrasLabel={locale === 'en' ? 'Figures' : 'Cifras'}
      paraQuienLabel={locale === 'en' ? "Who it's for" : 'Para quién'}
      enlacesLabel={locale === 'en' ? 'Links' : 'Enlaces'}
      {...c}
    />
  );
}
